import React, { useEffect, useRef } from 'react';
import './Quotes.css';

interface Quote {
  text: string;
  author: string;
  context: string;
  source?: string;
  category: 'personal' | 'education' | 'community' | 'artistic' | 'media';
}

const Quotes: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);

  const quotes: Quote[] = [
    {
      text: "Through art, I strive to capture the romance and mystery of the night, where earth meets sky in perfect harmony.",
      author: "Hector Díaz",
      context: "On his artistic philosophy",
      category: 'personal'
    },
    {
      text: "Hector's work represents the soul of our community - a bridge between dreams and reality, between darkness and light.",
      author: "Dr. Sharon McGavick",
      context: "Educational Leader's perspective on New DAY program",
      source: "Community Recognition Video",
      category: 'education'
    },
    {
      text: "His gallery became more than just a space for art - it became a beacon of hope for young artists seeking their voice.",
      author: "Local Art Critic",
      context: "On Hector's community impact",
      source: "Sound Life Magazine",
      category: 'media'
    },
    {
      text: "The New DAY program has transformed countless young lives, giving them not just artistic skills, but confidence and purpose.",
      author: "Community Leader",
      context: "Reflecting on youth program impact",
      source: "City of Destiny Award Ceremony",
      category: 'community'
    },
    {
      text: "Hector's vision of the future is painted with the wisdom of the past and the hope of tomorrow.",
      author: "Art Historian",
      context: "Analyzing the 'Art of the Future' collection",
      category: 'artistic'
    },
    {
      text: "Innovative and rewarding program... a generation of young artists will remember where their interest and opportunities came from.",
      author: "Michael S. Sullivan",
      context: "Cultural Resources Division Manager",
      source: "City of Tacoma",
      category: 'community'
    },
    {
      text: "Students became more positive and productive through Hector's comprehensive impact beyond art.",
      author: "Cenobio Macias",
      context: "Superintendent",
      source: "Tacoma Public Schools",
      category: 'education'
    },
    {
      text: "Program is beneficial to our community through bilingual teaching abilities and community impact.",
      author: "Natalya K. Paloma-Quintal",
      context: "Director",
      source: "Centro Latino",
      category: 'community'
    },
    {
      text: "Art without boundaries - Hector's work transcends cultural and linguistic barriers to speak to the universal human experience.",
      author: "International Art Review",
      context: "Featured artist profile",
      source: "International Artist Magazine",
      category: 'media'
    },
    {
      text: "Finding Light in the Darkness - Hector's nocturnal landscapes reveal the hidden beauty that emerges when the sun sets.",
      author: "Art Journal Editorial",
      context: "On artistic style and technique",
      source: "Contemporary Art Quarterly",
      category: 'artistic'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.fade-in-element');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'personal':
        return "M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 7V9L21 9ZM3 9V7L9 7V9L3 9Z";
      case 'education':
        return "M12 3L1 9L12 15L21 10.09V17H23V9L12 3ZM5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18Z";
      case 'community':
        return "M16 4C18.2 4 20 5.8 20 8C20 10.2 18.2 12 16 12C13.8 12 12 10.2 12 8C12 5.8 13.8 4 16 4ZM8 4C10.2 4 12 5.8 12 8C12 10.2 10.2 12 8 12C5.8 12 4 10.2 4 8C4 5.8 5.8 4 8 4ZM8 13C10.67 13 16 14.33 16 17V20H0V17C0 14.33 5.33 13 8 13Z";
      case 'artistic':
        return "M12 2L13.5 7.5L19 9L13.5 10.5L12 15L10.5 10.5L5 9L10.5 7.5L12 2Z";
      case 'media':
        return "M12 3L2 12H5V20H9V14H15V20H19V12H22L12 3Z";
      default:
        return "M14,17H17L19,13V7H13V13H16M6,17H9L11,13V7H5V13H8L6,17Z";
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'personal': return 'personal';
      case 'education': return 'education';
      case 'community': return 'community';
      case 'artistic': return 'artistic';
      case 'media': return 'media';
      default: return 'default';
    }
  };

  const featuredQuote = quotes[0];
  const otherQuotes = quotes.slice(1);

  return (
    <section className="quotes-section legacy-quotes-section" id="legacy" ref={sectionRef}>
      {/* Background Effects */}
      <div className="quotes-background">
        <div className="cosmic-orb orb-1"></div>
        <div className="cosmic-orb orb-2"></div>
        <div className="cosmic-orb orb-3"></div>
        <div className="starfield">
          {[...Array(25)].map((_, i) => (
            <div key={i} className={`star star-${i + 1}`}></div>
          ))}
        </div>
      </div>

      <div className="quotes-container">
        {/* Header Section */}
        <div className="quotes-header fade-in-element">
          <div className="header-badge">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14,17H17L19,13V7H13V13H16M6,17H9L11,13V7H5V13H8L6,17Z" fill="currentColor"/>
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.5"/>
            </svg>
            <span>Voices of Legacy</span>
          </div>
          <h1 className="quotes-title">Words That Inspire</h1>
          <p className="quotes-subtitle">
            Capturing the essence and transformative impact of Hector Díaz's artistic journey through the voices of those he touched
          </p>
          <div className="title-decoration">
            <div className="decoration-line"></div>
            <div className="decoration-star">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L13.5 7.5L19 9L13.5 10.5L12 15L10.5 10.5L5 9L10.5 7.5L12 2Z" fill="currentColor"/>
              </svg>
            </div>
            <div className="decoration-line"></div>
          </div>
        </div>

        {/* Featured Quote */}
        <div className="featured-quote fade-in-element">
          <div className="featured-quote-container">
            <div className="quote-glow"></div>
            <div className="featured-quote-card">
              <div className="quote-corners">
                <div className="corner top-left"></div>
                <div className="corner top-right"></div>
                <div className="corner bottom-left"></div>
                <div className="corner bottom-right"></div>
              </div>
              <div className="quote-icon">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14,17H17L19,13V7H13V13H16M6,17H9L11,13V7H5V13H8L6,17Z" fill="currentColor"/>
                </svg>
              </div>
              <blockquote className="featured-text">
                "{featuredQuote.text}"
              </blockquote>
              <div className="featured-attribution">
                <div className="attribution-line"></div>
                <cite className="featured-author">{featuredQuote.author}</cite>
                <span className="featured-context">{featuredQuote.context}</span>
              </div>
            </div>
          </div>
        </div>

        {/* All Quotes Grid */}
        <div className="quotes-grid fade-in-element">
          <div className="grid-container">
            {otherQuotes.map((quote, index) => (
              <div
                key={index}
                className="quote-card"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="card-shimmer"></div>
                <div className="card-header">
                  <div className={`category-badge ${getCategoryColor(quote.category)}`}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d={getCategoryIcon(quote.category)} fill="currentColor"/>
                    </svg>
                    <span>{quote.category.charAt(0).toUpperCase() + quote.category.slice(1)}</span>
                  </div>
                  <div className="quote-symbol">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14,17H17L19,13V7H13V13H16M6,17H9L11,13V7H5V13H8L6,17Z" fill="currentColor"/>
                    </svg>
                  </div>
                </div>
                <blockquote className="quote-text">
                  "{quote.text}"
                </blockquote>
                <div className="quote-footer">
                  <div className="author-info">
                    <div className="author-name">{quote.author}</div>
                    <div className="author-title">{quote.context}</div>
                    {quote.source && (
                      <div className="author-source">{quote.source}</div>
                    )}
                  </div>
                  <div className="verification-badge">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L13.5 7.5L19 9L13.5 10.5L12 15L10.5 10.5L5 9L10.5 7.5L12 2Z" fill="currentColor"/>
                    </svg>
                  </div>
                </div>
                <div className="card-accent"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Legacy Summary */}
        <div className="legacy-summary fade-in-element">
          <div className="summary-container">
            <div className="summary-icon">
              <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M8 12L10 14L16 8" stroke="currentColor" strokeWidth="2" fill="none"/>
                <circle cx="12" cy="12" r="6" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/>
              </svg>
            </div>
            <h3 className="summary-title">A Living Legacy</h3>
            <p className="summary-text">
              Hector Díaz's influence continues to inspire artists, educators, and community leaders.
              His vision of art as a transformative force lives on through the countless lives he touched
              and the artistic community he helped build in Tacoma.
            </p>
            <div className="legacy-stats">
              <div className="stat-item">
                <span className="stat-number">∞</span>
                <span className="stat-label">Lives Inspired</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">25+</span>
                <span className="stat-label">Years of Impact</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">1</span>
                <span className="stat-label">Artistic Legacy</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Quotes;